import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { DollarSign, TrendingUp, ArrowRight, Building2 } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import AnimatedCounter from '@/components/ui/animated-counter';

interface CostSavingsSummaryWidgetProps {
  agencyId?: string | null;
  agencyName?: string | null;
  hospitalVisitCost?: number | null;
  startDate?: Date;
  endDate?: Date;
}

interface SavingsData {
  totalSessions: number;
  homeCases: number;
  pcpCases: number;
  urgentCareCases: number;
  erCases: number;
}

export default function CostSavingsSummaryWidget({ agencyId, agencyName, hospitalVisitCost, startDate, endDate }: CostSavingsSummaryWidgetProps) {
  const navigate = useNavigate();
  const isAllAgencies = !agencyId;
  const [searchParams] = useSearchParams();
  const [data, setData] = useState<SavingsData | null>(null);
  const [loading, setLoading] = useState(true);

  // Default costs - matching hospital_visit_cost_defaults table
  const erCost = hospitalVisitCost || 2500;
  const homeCost = 150;
  const pcpCost = 450;
  const urgentCareCost = 500;

  useEffect(() => {
    const loadData = async () => {
      try {
        const { data: result, error } = await supabase.rpc('get_cost_savings_analytics', {
          _agency_id: agencyId || null,
          _start_date: startDate ? startDate.toISOString().split('T')[0] : null,
          _end_date: endDate ? endDate.toISOString().split('T')[0] : null
        });

        if (error) throw error;

        if (result && typeof result === 'object' && !Array.isArray(result)) {
          const typedResult = result as Record<string, unknown>;
          setData({
            totalSessions: (typedResult.totalSessions as number) || 0,
            homeCases: (typedResult.homeCases as number) || 0,
            pcpCases: (typedResult.pcpCases as number) || 0,
            urgentCareCases: (typedResult.urgentCareCases as number) || 0,
            erCases: (typedResult.erCases as number) || 0
          });
        }
      } catch (error) {
        console.error('Error loading cost savings summary:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [agencyId, startDate, endDate]);

  // Calculate total savings
  const calculateSavings = () => {
    if (!data) return 0;
    
    const divertedCases = data.homeCases + data.pcpCases + data.urgentCareCases;
    const actualCost = (data.homeCases * homeCost) + (data.pcpCases * pcpCost) + (data.urgentCareCases * urgentCareCost);
    const potentialErCost = divertedCases * erCost;
    
    return potentialErCost - actualCost;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const handleViewDetails = () => {
    // Preserve impersonation params when navigating
    const params = new URLSearchParams();
    if (searchParams.get('impersonate')) {
      params.set('impersonate', searchParams.get('impersonate')!);
    }
    if (searchParams.get('aid')) {
      params.set('aid', searchParams.get('aid')!);
    }
    const queryString = params.toString();
    navigate(`/admin/analytics${queryString ? `?${queryString}` : ''}`);
  };

  if (loading) {
    return (
      <Card className="shadow-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-green-600" />
            Cost Savings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-2">
            <div className="h-8 bg-muted rounded w-3/4"></div>
            <div className="h-4 bg-muted rounded w-1/2"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return null;
  }

  const totalSavings = calculateSavings();
  const divertedCases = data.homeCases + data.pcpCases + data.urgentCareCases;
  const divertedPercentage = data.totalSessions > 0 
    ? Math.round((divertedCases / data.totalSessions) * 100) 
    : 0;

  return (
    <Card className="shadow-sm bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center justify-between">
          <span className="flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-green-600" />
            Cost Savings Summary
          </span>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="gap-1 text-xs font-normal bg-white/60">
              <Building2 className="h-3 w-3" />
              {isAllAgencies ? 'All Agencies' : agencyName || 'Agency'}
            </Badge>
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-xs h-7 px-2"
              onClick={handleViewDetails}
            >
              View Details <ArrowRight className="ml-1 h-3 w-3" />
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <div className="text-3xl font-bold text-green-700">
            <AnimatedCounter 
              value={totalSavings} 
              duration={1500}
              formatOptions={{
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
              }}
            />
          </div>
          <p className="text-xs text-gray-600">Total Estimated Savings</p>
        </div>
        
        <div className="grid grid-cols-2 gap-4 pt-2 border-t border-green-200">
          <div>
            <div className="flex items-center gap-1">
              <TrendingUp className="h-3 w-3 text-green-600" />
              <span className="text-lg font-semibold text-gray-900">
                <AnimatedCounter value={divertedCases} duration={1200} />
              </span>
            </div>
            <p className="text-xs text-gray-600">Cases Diverted from ER</p>
          </div>
          <div>
            <div className="text-lg font-semibold text-gray-900">
              <AnimatedCounter value={divertedPercentage} duration={1000} suffix="%" />
            </div>
            <p className="text-xs text-gray-600">Diversion Rate</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
